/**
 * What each email IS, and the one honest count (Mailroom v2 PR 3).
 *
 * Six types (server: services/mailbox/mailType.ts), each with its own icon and
 * colour so the office can tell them apart without reading — the approved
 * mockup's "visual first" rule. Orange is spent ONLY on "a person is needed":
 * the "N need you" pill.
 *
 * HonestCountBar replaces the old yellow "1944 need attention" banner:
 *   - on the Mailroom team → "12 need you · oldest 2 h" (orange), plus the
 *     team picture;
 *   - off the team (Corbin) → the team's numbers in grey, never a debt.
 * The type strip under it shows every open type with its count, so nothing is
 * hidden — FYI is shown as "quiet", never counted.
 */
import type { ReactNode } from "react";
import { FilePlus2, MessageSquare, Landmark, MailX, Truck, Info, CircleHelp, Users } from "lucide-react";
import { MAIL_TYPE_ORDER, type MailCount, type MailType } from "../../lib/mail-count";

type TypeKey = MailType | "unsorted";

export const MAIL_TYPE_LOOK: Record<TypeKey, { label: string; Icon: typeof Info; tone: string; tile: string }> = {
    work_order: {
        label: "New work order",
        Icon: FilePlus2,
        tone: "text-sky-800 dark:text-sky-300",
        tile: "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300",
    },
    customer: {
        label: "Customer reply",
        Icon: MessageSquare,
        tone: "text-emerald-800 dark:text-emerald-300",
        tile: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300",
    },
    city: {
        label: "City / registration",
        Icon: Landmark,
        tone: "text-violet-800 dark:text-violet-300",
        tile: "bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300",
    },
    bounce: {
        label: "Didn't arrive",
        Icon: MailX,
        tone: "text-red-800 dark:text-red-300",
        tile: "bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300",
    },
    vendor: {
        label: "Vendor / PO",
        Icon: Truck,
        tone: "text-teal-800 dark:text-teal-300",
        tile: "bg-teal-100 text-teal-800 dark:bg-teal-500/15 dark:text-teal-300",
    },
    fyi: {
        label: "FYI / spam",
        Icon: Info,
        tone: "text-muted-foreground",
        tile: "bg-muted text-muted-foreground",
    },
    unsorted: {
        label: "Not sorted yet",
        Icon: CircleHelp,
        tone: "text-muted-foreground",
        tile: "bg-muted text-muted-foreground",
    },
};

/** The row's type: icon + the office's word for it. Falls back to `fallback` when untyped. */
export function MailTypeChip({ type, fallback }: { type?: MailType | null; fallback?: ReactNode }) {
    if (!type) return <>{fallback ?? null}</>;
    const look = MAIL_TYPE_LOOK[type];
    const { Icon } = look;
    return (
        <span className={`inline-flex items-center gap-1 font-semibold ${look.tone}`}>
            <Icon className="h-3 w-3 flex-none" />
            {look.label}
        </span>
    );
}

export function HonestCountBar({
    count,
    active,
    onPick,
    switchOn,
    onSwitch,
    hideTypes = false,
}: {
    count: MailCount | undefined;
    /** New Mailroom (PR 6): the type the list is filtered to. Absent = the strip is display-only. */
    active?: MailType | "all";
    onPick?: (t: MailType | "all") => void;
    /** The per-person "Try the new Mailroom" switch. */
    switchOn?: boolean;
    onSwitch?: () => void;
    /** The friendly list (10/9) draws its own type strip under its chips. */
    hideTypes?: boolean;
}) {
    if (!count) return null;
    const pick = (t: MailType | "all") => onPick?.(active === t ? "all" : t);
    const team = count.team;
    const people = team.byAssignee.map((a) => `${a.name.split(" ")[0]} ${a.count}`).join(" · ");
    return (
        <div className="rounded-2xl border border-border bg-card px-3 py-2.5">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                {/* On a phone the friendly list's big chips already carry these
                    numbers (10/9), so the bar keeps only its two buttons. */}
                <div className={hideTypes ? "hidden flex-wrap items-center gap-x-4 gap-y-2 sm:flex" : "contents"}>
                    {count.onTeam ? (
                        count.needYou > 0 ? (
                            <span
                                className="inline-flex items-center gap-2 rounded-full bg-orange-50 py-1 pl-1 pr-3 text-sm font-extrabold text-orange-800 dark:bg-orange-500/15 dark:text-orange-200"
                                title="Emails nobody has taken yet, plus the ones assigned to you. FYI and spam never count."
                            >
                                <span className="grid h-7 min-w-7 place-items-center rounded-full bg-orange-700 px-2 text-sm tabular-nums text-white">
                                    {count.needYou.toLocaleString()}
                                </span>
                                need you
                                {count.oldestWaitingLabel && (
                                    <span className="font-semibold">· oldest {count.oldestWaitingLabel}</span>
                                )}
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-2 text-sm font-bold text-emerald-700 dark:text-emerald-400">
                                Nothing needs you
                            </span>
                        )
                    ) : (
                        <span
                            className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground"
                            title="You are not on the Mailroom team, so nothing here is counted against you."
                        >
                            <Users className="h-4 w-4" />
                            Team: {team.open.toLocaleString()} open · {team.unassigned.toLocaleString()} not taken
                        </span>
                    )}
                    {team.waiting > 0 && (
                        <span
                            className="text-xs font-semibold text-muted-foreground"
                            title="We wrote last — the ball is in their court"
                        >
                            Waiting on them: {team.waiting.toLocaleString()}
                        </span>
                    )}
                    {people && (
                        <span className="text-xs font-semibold text-muted-foreground" title="Assigned, by person">
                            Taken: {people}
                        </span>
                    )}
                </div>
                {onSwitch && (
                    <button
                        type="button"
                        onClick={onSwitch}
                        aria-pressed={!!switchOn}
                        className={`ml-auto rounded-full border border-border px-3 py-1 text-xs font-semibold text-muted-foreground hover:bg-muted hover:text-foreground`}
                        title="Switch between the new Mailroom and the old one. Only changes it for you."
                    >
                        {switchOn ? "Back to the old Mailroom" : "Try the new Mailroom"}
                    </button>
                )}
            </div>
            {!hideTypes && (
                <div className="mt-2 flex flex-wrap gap-1.5" aria-label="Open email by type">
                    {MAIL_TYPE_ORDER.filter((t) => t.key !== "unsorted" || count.byType.unsorted.open > 0).map((t) => {
                        const look = MAIL_TYPE_LOOK[t.key];
                        const c = count.byType[t.key];
                        const { Icon } = look;
                        const clickable = !!onPick && t.key !== "unsorted";
                        const on = active === t.key;
                        const Tag = clickable ? "button" : "span";
                        return (
                            <Tag
                                key={t.key}
                                {...(clickable
                                    ? {
                                          type: "button" as const,
                                          onClick: () => pick(t.key as MailType),
                                          "aria-pressed": on,
                                      }
                                    : {})}
                                className={`inline-flex items-center gap-1.5 rounded-lg border bg-card py-1 pl-1 pr-2.5 text-xs ${
                                    on ? "border-foreground ring-1 ring-foreground" : "border-border"
                                } ${clickable ? "hover:bg-muted" : ""}`}
                                title={`${look.label}: ${c.open} open, ${c.unassigned} not taken${clickable ? " — click to show only these" : ""}`}
                            >
                                <span className={`grid h-6 w-6 place-items-center rounded-md ${look.tile}`}>
                                    <Icon className="h-3.5 w-3.5" />
                                </span>
                                <span className="font-semibold text-foreground">{t.short}</span>
                                <span className="font-extrabold tabular-nums text-foreground">
                                    {c.open.toLocaleString()}
                                </span>
                            </Tag>
                        );
                    })}
                    {onPick && active && active !== "all" && (
                        <button
                            type="button"
                            onClick={() => onPick("all")}
                            className="rounded-lg px-2 py-1 text-xs font-semibold text-muted-foreground underline hover:text-foreground"
                        >
                            Show everything
                        </button>
                    )}
                    <span
                        className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border py-1 pl-1 pr-2.5 text-xs text-muted-foreground"
                        title="FYI and spam still in the inbox. Shown so nothing hides; never counted."
                    >
                        <span className={`grid h-6 w-6 place-items-center rounded-md ${MAIL_TYPE_LOOK.fyi.tile}`}>
                            <Info className="h-3.5 w-3.5" />
                        </span>
                        FYI {count.quiet.toLocaleString()} quiet
                    </span>
                </div>
            )}
        </div>
    );
}
